import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { Users, Plus, Trash2, Radar, Copy } from "lucide-react";

const API = "/api";

const GROUPS = ["FAMILY", "FRIEND", "WORK", "COLLEAGUE", "CREW"];

function loadLocalContacts() {
  try {
    const raw = localStorage.getItem("mirrors_contacts_local_v1");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalContacts(items) {
  localStorage.setItem("mirrors_contacts_local_v1", JSON.stringify(items));
}

export default function MirrorsContacts({ mirrorsId }) {
  const [nickname, setNickname] = useState("");
  const [contactId, setContactId] = useState("");
  const [group, setGroup] = useState("FRIEND");

  const [remoteContacts, setRemoteContacts] = useState([]);
  const [localContacts, setLocalContacts] = useState(() => loadLocalContacts());
  const [isLoading, setIsLoading] = useState(false);

  const combined = useMemo(() => {
    const local = Array.isArray(localContacts) ? localContacts : [];
    const remote = Array.isArray(remoteContacts) ? remoteContacts : [];

    const byKey = new Map();
    for (const c of remote) byKey.set(`remote:${c.id}`, { ...c, _source: "remote" });
    for (const c of local) byKey.set(`local:${c.id}`, { ...c, _source: "local" });
    return Array.from(byKey.values());
  }, [localContacts, remoteContacts]);

  const refresh = async () => {
    if (!mirrorsId) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/users/${encodeURIComponent(mirrorsId)}/contacts`);
      setRemoteContacts(res.data?.contacts || []);
    } catch {
      setRemoteContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    saveLocalContacts(localContacts);
  }, [localContacts]);

  useEffect(() => {
    if (mirrorsId) {
      refresh();
    }
  }, [mirrorsId]);

  const add = async () => {
    const clean = contactId.trim();
    if (!clean) {
      toast.error("Enter a MIRRORS ID");
      return;
    }

    // local add always
    const newLocal = {
      id: crypto.randomUUID(),
      owner_mirrors_id: mirrorsId || "",
      contact_mirrors_id: clean,
      nickname: nickname.trim() || null,
      group,
      created_at: new Date().toISOString(),
    };
    setLocalContacts((p) => [newLocal, ...(p || [])]);

    // remote add if we have mirrorsId
    if (mirrorsId) {
      try {
        await axios.post(`${API}/users/${encodeURIComponent(mirrorsId)}/contacts`, {
          contact_mirrors_id: clean,
          nickname: nickname.trim() || null,
          group,
        });
        toast.success("Contact added");
        await refresh();
      } catch {
        toast.message("Saved locally (server sync unavailable)");
      }
    } else {
      toast.message("Saved locally");
    }

    setContactId("");
    setNickname("");
  };

  const remove = async (c) => {
    if (c._source === "local") {
      setLocalContacts((p) => (p || []).filter((x) => x.id !== c.id));
      return;
    }

    if (!mirrorsId) return;
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(mirrorsId)}/contacts/${c.id}`);
      toast.message("Removed");
      await refresh();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const discoverNearby = () => {
    toast.message(
      "Nearby discovery requires iOS native (MultipeerConnectivity). Web shows online roster in Live Sync."
    );
  };

  return (
    <Card className="bg-black/70 border-[#28880D]/30" data-testid="mirrors-contacts-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="font-mono text-[#28880D] tracking-wider flex items-center gap-2">
            <Users className="h-4 w-4" /> CONTACTS
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-[#28880D]/40 text-[#28880D] hover:bg-[#001a00] font-mono"
              onClick={discoverNearby}
              data-testid="contacts-discover-nearby"
            >
              <Radar className="h-4 w-4 mr-2" />
              DISCOVER_NEARBY
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-[#28880D]/40 text-[#28880D] hover:bg-[#001a00] font-mono"
              onClick={refresh}
              disabled={isLoading || !mirrorsId}
              title={!mirrorsId ? "Generate/Create a MIRRORS ID to sync contacts" : "Refresh"}
              data-testid="contacts-refresh"
            >
              REFRESH
            </Button>
          </div>
        </div>
        <div className="mt-1 text-xs font-mono text-[#ADB0AC]">
          Add MIRRORS IDs here. Stored locally + synced to your account when available.
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="contacts-form">
          <div className="md:col-span-1">
            <div className="text-[11px] font-mono text-[#ADB0AC]">MIRRORS_ID</div>
            <Input
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              placeholder="MIRRORS-XXXX"
              className="bg-black border-[#28880D]/30 text-[#28880D] placeholder:text-[#28880D]/40 font-mono"
              data-testid="contacts-input-id"
            />
          </div>
          <div className="md:col-span-1">
            <div className="text-[11px] font-mono text-[#ADB0AC]">NICKNAME</div>
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Optional"
              className="bg-black border-[#28880D]/30 text-[#28880D] placeholder:text-[#28880D]/40 font-mono"
              data-testid="contacts-input-nickname"
            />
          </div>
          <div className="md:col-span-1">
            <div className="text-[11px] font-mono text-[#ADB0AC]">GROUP</div>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger
                className="bg-black border-[#28880D]/30 text-[#28880D] font-mono"
                data-testid="contacts-group-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black border-[#28880D]/30">
                {GROUPS.map((g) => (
                  <SelectItem
                    key={g}
                    value={g}
                    className="font-mono"
                    data-testid={`contacts-group-${g.toLowerCase()}`}
                  >
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button
              onClick={add}
              className="w-full bg-[#28880D] text-black hover:bg-[#28880D]/90 font-mono"
              data-testid="contacts-add-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              ADD
            </Button>
          </div>
        </div>

        <Separator className="bg-[#28880D]/20" />

        <div className="space-y-2" data-testid="contacts-list">
          {combined.length === 0 ? (
            <div className="text-sm font-mono text-[#ADB0AC]" data-testid="contacts-empty">
              No contacts yet.
            </div>
          ) : (
            combined.slice(0, 30).map((c) => (
              <div
                key={`${c._source}:${c.id}`}
                className="flex items-center justify-between gap-3 rounded border px-3 py-2"
                style={{ borderColor: "rgba(40,136,13,0.25)", background: "rgba(0,0,0,0.35)" }}
                data-testid={`contacts-item-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="truncate font-mono text-[#28880D] text-sm" data-testid={`contacts-item-id-${c.id}`}>
                      {c.contact_mirrors_id}
                    </div>
                    {c.group ? (
                      <Badge className="bg-[#28880D]/15 text-[#28880D] border border-[#28880D]/25 font-mono">
                        {c.group}
                      </Badge>
                    ) : null}
                    <Badge className="bg-[#ADB0AC]/10 text-[#ADB0AC] border border-[#ADB0AC]/20 font-mono">
                      {c._source.toUpperCase()}
                    </Badge>
                  </div>
                  {c.nickname ? (
                    <div
                      className="text-[11px] font-mono text-[#ADB0AC] truncate"
                      data-testid={`contacts-item-nickname-${c.id}`}
                    >
                      {c.nickname}
                    </div>
                  ) : null}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#28880D]/40 text-[#28880D] hover:bg-[#001a00] font-mono"
                    onClick={() => {
                      navigator.clipboard.writeText(String(c.contact_mirrors_id));
                      toast.message("Copied");
                    }}
                    data-testid={`contacts-copy-${c.id}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#C41E3A]/40 text-[#C41E3A] hover:bg-[#C41E3A]/10 font-mono"
                    onClick={() => remove(c)}
                    data-testid={`contacts-remove-${c.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
